/**
 * Help — Phase 4 user manual.
 *
 * Layout:
 *   - Sticky sidebar (left) with section list + search + Print button.
 *   - Main scroll area renders markdown sections using react-markdown.
 *   - Scroll-spy: the sidebar entry highlights when the matching `<section>`
 *     intersects the viewport. Driven by an IntersectionObserver.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import {
  BookOpen,
  LogIn,
  Upload,
  LayoutDashboard,
  Trophy,
  Award,
  Scale,
  TrendingUp,
  PieChart,
  Flame,
  TableProperties,
  Boxes,
  Combine,
  Building2,
  Users,
  Share2,
  Tv,
  ShieldAlert,
  Languages,
  HelpCircle,
  Wrench,
  LifeBuoy,
  Zap,
  Search,
  Printer,
  Lightbulb,
  AlertTriangle,
} from "lucide-react";
import manualEs from "@/help/manual.es";
import manualEn from "@/help/manual.en";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import ShareAppButton from "@/components/ShareAppButton";

const ICON_MAP = {
  BookOpen,
  LogIn,
  Upload,
  LayoutDashboard,
  Trophy,
  Award,
  Scale,
  TrendingUp,
  PieChart,
  Flame,
  TableProperties,
  Boxes,
  Zap,
  Combine,
  Building2,
  Users,
  Share2,
  Tv,
  ShieldAlert,
  Languages,
  HelpCircle,
  Wrench,
  LifeBuoy,
};

export default function Help() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "es";
  const manual = lang === "en" ? manualEn : manualEs;
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(manual[0]?.id || "");
  const sectionRefs = useRef({});
  const scrollContainerRef = useRef(null);

  // Filter sections by search (title or body match).
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return manual;
    return manual.filter(
      (sec) =>
        sec.title.toLowerCase().includes(s) ||
        (sec.intro && sec.intro.toLowerCase().includes(s)) ||
        sec.body.toLowerCase().includes(s)
    );
  }, [manual, search]);

  // Scroll-spy: observe the section heads.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the most visible intersecting entry.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-100px 0px -60% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [filtered.length]);

  const goTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Update the URL hash without re-triggering the observer hard.
      window.history.replaceState(null, "", `#${id}`);
    }
  };

  // On first mount, honor a URL hash like `/help#dashboard`.
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (hash && sectionRefs.current[hash]) {
      // Defer to allow layout to settle.
      setTimeout(() => goTo(hash), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => window.print();

  return (
    <div className="space-y-4 print:space-y-0" data-testid="help-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 print:hidden">
        <div>
          <h1
            className="flex items-center gap-2 text-3xl font-semibold tracking-tight"
            data-testid="help-title"
          >
            <BookOpen className="h-7 w-7" />
            {t("help.title")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("help.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <ShareAppButton variant="compact" testId="help-share-app-btn" />
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            data-testid="help-print-btn"
          >
            <Printer className="mr-2 h-3.5 w-3.5" />
            {t("help.print")}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_1fr] print:block">
        {/* Sidebar */}
        <aside
          className="space-y-3 print:hidden"
          data-testid="help-sidebar"
        >
          <div className="sticky top-20 space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("help.searchPlaceholder")}
                className="h-9 pl-8"
                data-testid="help-search"
              />
            </div>
            <ScrollArea className="h-[calc(100vh-220px)] rounded-lg border border-border bg-card/40">
              <nav className="p-2">
                {filtered.length === 0 ? (
                  <div className="p-3 text-center text-xs text-muted-foreground">
                    {t("help.noResults")}
                  </div>
                ) : (
                  filtered.map((sec) => {
                    const Icon = ICON_MAP[sec.icon] || BookOpen;
                    const active = sec.id === activeId;
                    return (
                      <button
                        key={sec.id}
                        type="button"
                        onClick={() => goTo(sec.id)}
                        data-testid={`help-toc-${sec.id}`}
                        className={`group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
                          active
                            ? "bg-accent text-accent-foreground font-medium"
                            : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{sec.title}</span>
                      </button>
                    );
                  })
                )}
              </nav>
            </ScrollArea>
          </div>
        </aside>

        {/* Main content */}
        <main
          ref={scrollContainerRef}
          className="space-y-12 print:space-y-6"
          data-testid="help-content"
        >
          {filtered.map((sec, idx) => {
            const Icon = ICON_MAP[sec.icon] || BookOpen;
            return (
              <section
                key={sec.id}
                id={sec.id}
                ref={(el) => (sectionRefs.current[sec.id] = el)}
                data-testid={`help-section-${sec.id}`}
                className="scroll-mt-20 border-b border-border/40 pb-10 last:border-b-0 last:pb-0"
              >
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-foreground">
                    <Icon className="h-4 w-4" />
                  </div>
                  <h2 className="text-2xl font-semibold tracking-tight">
                    <span className="text-muted-foreground">
                      {String(idx + 1).padStart(2, "0")}.
                    </span>{" "}
                    {sec.title}
                  </h2>
                </div>
                {sec.intro && (
                  <p className="mb-4 text-base text-muted-foreground">
                    {sec.intro}
                  </p>
                )}
                <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:font-semibold prose-headings:tracking-tight prose-h3:text-base prose-h3:mt-6 prose-h3:mb-2 prose-p:leading-relaxed prose-table:text-xs prose-code:text-[0.85em] prose-code:bg-muted prose-code:rounded prose-code:px-1 prose-code:py-0.5 prose-code:before:content-none prose-code:after:content-none prose-pre:bg-muted prose-pre:border prose-pre:border-border">
                  <ReactMarkdown>{sec.body}</ReactMarkdown>
                </div>
                {sec.tips?.map((tip, i) => (
                  <div
                    key={i}
                    className={`mt-4 flex items-start gap-2 rounded-md border p-3 text-sm ${
                      tip.kind === "warning"
                        ? "border-destructive/40 bg-destructive/10 text-foreground"
                        : "border-primary/40 bg-primary/10 text-foreground"
                    }`}
                    data-testid={`help-tip-${sec.id}-${i}`}
                  >
                    {tip.kind === "warning" ? (
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    ) : (
                      <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    )}
                    <p className="leading-relaxed">{tip.text}</p>
                  </div>
                ))}
              </section>
            );
          })}
        </main>
      </div>
    </div>
  );
}
